"use client";

/**
 * PlatformWorkspaceGrid — every workspace as a dense card grid plus
 * a monogram marquee under it. Lives on /platform only.
 *
 * Reads APPS from apps-registry. Each card is small (3-up to 5-up
 * responsive), with the monogram glyph + status dot + tagline. Each
 * card links to /apps/[slug].
 *
 * The marquee underneath echoes the home-page rhythm but pulls only
 * the shipping/beta apps so it doesn't lie.
 */
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { APPS } from "@/lib/apps-registry";
import { Marquee } from "./marquee";
import { ScrollReveal, GlowCard } from "./animations";

function monogram(name: string, accent?: string) {
  if (accent) return accent;
  return name.slice(0, 2);
}

function statusTone(s: "shipping" | "beta" | "soon") {
  switch (s) {
    case "shipping":
      return { dot: "bg-foreground/85", label: "Available" };
    case "beta":
      return { dot: "bg-[hsl(var(--accent))]", label: "Beta" };
    case "soon":
      return { dot: "bg-foreground/30", label: "Soon" };
  }
}

export function PlatformWorkspaceGrid() {
  const shipping = APPS.filter((a) => a.status !== "soon");

  return (
    <section className="border-b hairline">
      <div className="container-x py-14 md:py-20">
        <div className="flex items-baseline justify-between border-b hairline pb-4">
          <h2 className="text-sm tracking-[0.18em] uppercase text-foreground/65">
            Workspaces
          </h2>
          <span className="font-mono text-xs text-foreground/65">
            {APPS.length} rooms · {shipping.length} open today
          </span>
        </div>

        <div className="mt-8 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <h3 className="display-serif text-3xl md:text-4xl">
              One workshop,{" "}
              <span className="text-[hsl(var(--accent))]">many</span> rooms.
            </h3>
            <p className="mt-4 text-foreground/70 leading-relaxed">
              The workshop has a room for the day-to-day, a room for serious
              writing, a room for code, a room for the news, a room for the
              numbers — and a long tail of specialist rooms for the work that
              doesn&#39;t fit anywhere else. Each one has its own door, its own
              tools, its own memory of what you did last time. The hallway
              between them is what makes it a workshop and not a tab graveyard.
            </p>
            <Link
              href="/apps"
              className="mt-6 inline-flex items-center gap-1.5 rounded-full border hairline-strong px-4 py-2 text-sm font-medium hover:bg-foreground/5"
            >
              Browse every workspace
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="col-span-12 md:col-span-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-foreground/10 rounded-xl overflow-hidden border hairline">
              {APPS.map((a, i) => {
                const tone = statusTone(a.status);
                return (
                  <ScrollReveal key={a.slug} delay={i * 22}>
                    <GlowCard
                      glow={180}
                      className="bg-background h-full"
                    >
                      <Link
                        href={`/apps/${a.slug}`}
                        className="group block p-4 h-full hover:bg-foreground/[0.025] transition"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md border hairline-strong font-mono text-[12px] tracking-[0.04em] uppercase">
                            {monogram(a.name, a.accent)}
                          </span>
                          <ArrowUpRight className="h-3 w-3 text-foreground/30 group-hover:text-foreground/85 transition" />
                        </div>
                        <div className="mt-4 text-base font-medium tracking-tight leading-tight">
                          {a.name}
                        </div>
                        <div className="mt-1 text-[11px] text-foreground/65 leading-snug line-clamp-2">
                          {a.tagline}
                        </div>
                        <div className="mt-3 flex items-center gap-1.5">
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${tone.dot}`}
                            aria-hidden
                          />
                          <span className="text-[9.5px] font-mono uppercase tracking-[0.16em] text-foreground/65">
                            {tone.label}
                          </span>
                        </div>
                      </Link>
                    </GlowCard>
                  </ScrollReveal>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t hairline">
        <Marquee speed={55}>
          {shipping.map((a) => (
            <span
              key={`m-${a.slug}`}
              className="text-2xl md:text-3xl display-serif text-foreground/70"
            >
              <Link href={`/apps/${a.slug}`}>{a.name}</Link>
              <span className="text-foreground/20 mx-5">·</span>
            </span>
          ))}
        </Marquee>
        <Marquee speed={70} reverse className="border-t hairline">
          {APPS.map((a) => (
            <span
              key={`mr-${a.slug}`}
              className="text-xl md:text-2xl font-mono uppercase tracking-[0.2em] text-foreground/35"
            >
              {a.domain}
              <span className="text-foreground/15 mx-5">·</span>
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}
